import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import {
  effectiveWorkflowStatus,
  type TaskId,
  type TaskWorkflowStatus,
} from '@bigint-project/core';

import { CollaborationBadge, useCollaborationSummary } from '../components/collaboration/TaskCollaborationPanel';
import type { BoardCard, BoardGroupMode, BoardResource } from '../components/board/types';
import type { TaskCollaborationPort } from '../platform/ports';
import { progressAsOf, selectExpectedProgressByTask } from '../integration/derivations';
import { useProjectStore } from '../store/project-store';

/** 看板只在切到這個檢視時才載入，讓 web 入口的首包不含它。 */
const TaskBoard = lazy(async () => ({
  default: (await import('../components/board/TaskBoard')).TaskBoard,
}));

export function BoardView({
  readOnly = false,
  collaboration,
}: {
  readonly readOnly?: boolean;
  /** 雲端宿主才有的留言與附件 port；undefined 時卡片不顯示協作徽章。 */
  readonly collaboration?: TaskCollaborationPort;
} = {}): ReactElement {
  const project = useProjectStore((state) => state.project);
  const selectTasks = useProjectStore((state) => state.selectTasks);
  const openTaskDetails = useProjectStore((state) => state.openTaskDetails);
  const openTaskProgress = useProjectStore((state) => state.openTaskProgress);
  const setTaskWorkflowStatus = useProjectStore((state) => state.setTaskWorkflowStatus);
  const reassignResource = useProjectStore((state) => state.reassignResource);

  const [groupMode, setGroupMode] = useState<BoardGroupMode>('status');
  const [filterResourceId, setFilterResourceId] = useState<string | null>(null);
  const [behindOnly, setBehindOnly] = useState(false);
  const [keyword, setKeyword] = useState('');

  const { summary, refresh } = useCollaborationSummary(collaboration ?? null);
  const isTaskDetailsOpen = useProjectStore((state) => state.isTaskDetailsOpen);
  const wasTaskDetailsOpen = useRef(false);

  // 留言與附件只在任務詳細對話框裡新增，關閉對話框就是唯一需要重抓計數的時機。
  useEffect(() => {
    if (wasTaskDetailsOpen.current && !isTaskDetailsOpen) refresh();
    wasTaskDetailsOpen.current = isTaskDetailsOpen;
  }, [isTaskDetailsOpen, refresh]);

  const resources = useMemo<readonly BoardResource[]>(
    () =>
      project.resources
        .filter((resource) => resource.type === 'Work' && resource.isBudget !== true)
        .map((resource) => ({ id: resource.id as string, name: resource.name })),
    [project.resources],
  );

  const cards = useMemo<readonly BoardCard[]>(() => {
    const expectedByTask = selectExpectedProgressByTask(project, progressAsOf(project));
    const parentIds = new Set(project.tasks.map((task) => task.parentId as string | null));
    const nameById = new Map(project.tasks.map((task) => [task.id as string, task.name]));
    const boardResourceIds = new Set(resources.map((resource) => resource.id));
    const resourceIdsByTask = new Map<string, string[]>();
    for (const assignment of project.assignments) {
      const resourceId = assignment.resourceId as string;
      if (!boardResourceIds.has(resourceId)) continue;
      const list = resourceIdsByTask.get(assignment.taskId as string);
      if (list === undefined) resourceIdsByTask.set(assignment.taskId as string, [resourceId]);
      else list.push(resourceId);
    }
    return project.tasks
      .filter((task) => !task.isSummary && !parentIds.has(task.id as string))
      .map((task) => {
        const expected = expectedByTask.get(task.id as string);
        return {
          taskId: task.id as string,
          wbs: task.wbs,
          name: task.name,
          parentName: task.parentId === null ? null : nameById.get(task.parentId as string) ?? null,
          start: task.start as string,
          finish: task.finish as string,
          percentComplete: task.percentComplete as number,
          expectedPercent: (expected?.expectedPercent as number | undefined) ?? 0,
          expectedStatus: expected?.status ?? 'notStarted',
          status: effectiveWorkflowStatus(task),
          resourceIds: resourceIdsByTask.get(task.id as string) ?? [],
        } satisfies BoardCard;
      });
  }, [project, resources]);

  const handleOpenDetails = useCallback(
    (taskId: string) => {
      selectTasks([taskId]);
      openTaskDetails();
    },
    [openTaskDetails, selectTasks],
  );

  const handleOpenProgress = useCallback(
    (taskId: string) => {
      selectTasks([taskId]);
      openTaskProgress();
    },
    [openTaskProgress, selectTasks],
  );

  const handleMoveToStatus = useCallback(
    (taskId: string, status: TaskWorkflowStatus) => {
      setTaskWorkflowStatus(taskId as TaskId, status);
    },
    [setTaskWorkflowStatus],
  );

  const handleMoveToResource = useCallback(
    (taskId: string, fromResourceId: string | null, toResourceId: string | null) => {
      const current = useProjectStore.getState().project;
      const source =
        fromResourceId === null
          ? undefined
          : current.assignments.find(
              (assignment) => assignment.taskId === taskId && (assignment.resourceId as string) === fromResourceId,
            );
      const target =
        toResourceId === null
          ? undefined
          : current.resources.find((resource) => (resource.id as string) === toResourceId);
      reassignResource(
        taskId as TaskId,
        source?.id ?? null,
        target?.id ?? null,
        source === undefined ? 1 : (source.units as number),
        source === undefined ? 0 : (source.work as number),
      );
    },
    [reassignResource],
  );

  const renderCardExtras = useCallback(
    (taskId: string) => {
      const counts = summary?.tasks[taskId];
      if (counts === undefined) return null;
      return <CollaborationBadge comments={counts.comments} attachments={counts.attachments} />;
    },
    [summary],
  );

  return (
    <Suspense fallback={<div className="p-4 text-sm text-fg-muted">看板載入中…</div>}>
      <TaskBoard
        cards={cards}
        resources={resources}
        groupMode={groupMode}
        onGroupModeChange={setGroupMode}
        filterResourceId={filterResourceId}
        onFilterResourceIdChange={setFilterResourceId}
        behindOnly={behindOnly}
        onBehindOnlyChange={setBehindOnly}
        keyword={keyword}
        onKeywordChange={setKeyword}
        readOnly={readOnly}
        onMoveToStatus={handleMoveToStatus}
        onMoveToResource={handleMoveToResource}
        onOpenDetails={handleOpenDetails}
        onOpenProgress={handleOpenProgress}
        renderCardExtras={collaboration === undefined ? undefined : renderCardExtras}
      />
    </Suspense>
  );
}
